import { useEffect, useState } from 'react';
import type { HolidayItem, HrMeResponse } from '@ip/shared-types';
import { useHrApi } from '@/services/api';

/**
 * Session-scoped caches: the HR context and public holidays are stable, so
 * hopping between the three HR surfaces never refetches them.
 */
const hrMeCache = new Map<string, HrMeResponse>();
const holidayCache = new Map<string, HolidayItem[]>(); // `${nip}:${year}`

/** The caller's per-company HR context (employee link, manager flag, hr-admin flag). */
export function useHrMe(nip: string) {
  const { hrMe } = useHrApi();
  const [data, setData] = useState<HrMeResponse | null>(() => hrMeCache.get(nip) ?? null);
  const [loading, setLoading] = useState(() => !hrMeCache.has(nip));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const cached = hrMeCache.get(nip);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    hrMe(nip)
      .then((res) => {
        hrMeCache.set(nip, res);
        if (mounted) {
          setData(res);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (mounted) setError(e instanceof Error ? e.message : 'Failed to load HR context');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [nip, hrMe]);

  return { hrMe: data, loading, error };
}

/**
 * Polish public holidays for the given years as a Map<YYYY-MM-DD, name> —
 * feeds calendar shading and the client-side working-day preview.
 */
export function useHolidays(nip: string, years: number[]) {
  const { holidays } = useHrApi();
  const [map, setMap] = useState<Map<string, string>>(() => new Map());
  const yearsKey = [...new Set(years)].sort((a, b) => a - b).join(',');

  useEffect(() => {
    let mounted = true;
    const wanted = yearsKey ? yearsKey.split(',').map(Number) : [];
    (async () => {
      const items: HolidayItem[] = [];
      for (const year of wanted) {
        const cacheKey = `${nip}:${year}`;
        let list = holidayCache.get(cacheKey);
        if (!list) {
          try {
            list = await holidays(nip, `${year}-01-01`, `${year}-12-31`);
            holidayCache.set(cacheKey, list);
          } catch {
            list = []; // shading degrades gracefully; the backend still validates
          }
        }
        items.push(...list);
      }
      if (mounted) setMap(new Map(items.map((h) => [h.date, h.name])));
    })();
    return () => {
      mounted = false;
    };
  }, [nip, yearsKey, holidays]);

  return map;
}
